import { Badge, Text, makeStyles, shorthands, tokens } from '@fluentui/react-components';
import type { ReportPreview } from '@worklogix/shared';

import { MOBILE, SHRINKABLE } from '../../responsive';

/**
 * "What the report will look like and what it will contain" (Michael, 2026-10-07).
 *
 * 🔥 THIS RENDERS A DOCUMENT, NOT A DATA GRID, and the difference is the point. The
 * question being answered is "is this the report I meant?" — which is settled by the
 * headline figures and the shape of the thing, not by scrolling rows. So it reads
 * top-down like the attendance write-up Michael sent as the model: a title, the period,
 * a summary table of measures, then the actual columns and real rows beneath.
 *
 * EVERY FIGURE HERE CAME FROM THE SERVER running the report's own query — see
 * `report-run.service.ts`. Nothing on this screen is computed locally from a sample,
 * because a summary derived from the 15 rows that happen to be previewed would be
 * confidently wrong about a 400-row report.
 *
 * AND IT SAYS WHAT IT IS NOT SHOWING. Every table prints "15 of 412 rows" and a report
 * split across more tabs than fit says how many were left out. A preview that silently
 * truncates teaches people to believe a number that is not there.
 */

const useStyles = makeStyles({
  page: {
    ...shorthands.padding('24px'),
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('20px'),
    ...SHRINKABLE,
    [MOBILE]: { ...shorthands.padding('14px') },
  },
  title: {
    fontSize: tokens.fontSizeHero700,
    lineHeight: tokens.lineHeightHero700,
    fontWeight: tokens.fontWeightSemibold,
    [MOBILE]: { fontSize: tokens.fontSizeBase600, lineHeight: tokens.lineHeightBase600 },
  },
  byline: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase300 },
  lede: { fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase400 },
  section: { display: 'flex', flexDirection: 'column', ...shorthands.gap('8px'), ...SHRINKABLE },
  heading: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    paddingBottom: '4px',
  },
  subHeading: {
    display: 'flex',
    alignItems: 'baseline',
    ...shorthands.gap('8px'),
    flexWrap: 'wrap',
  },
  tabName: { fontWeight: tokens.fontWeightSemibold },
  muted: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  scroller: { overflowX: 'auto', WebkitOverflowScrolling: 'touch', ...SHRINKABLE },
  table: {
    borderCollapse: 'collapse',
    width: '100%',
    fontSize: tokens.fontSizeBase200,
  },
  th: {
    textAlign: 'left',
    whiteSpace: 'nowrap',
    ...shorthands.padding('6px', '10px'),
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
    fontWeight: tokens.fontWeightSemibold,
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  td: {
    ...shorthands.padding('6px', '10px'),
    whiteSpace: 'nowrap',
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke3),
  },
  numeric: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  /** The Measure | Value block the attendance write-up opens with. */
  measureKey: {
    ...shorthands.padding('6px', '10px'),
    color: tokens.colorNeutralForeground2,
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke3),
    width: '60%',
  },
  measureValue: {
    ...shorthands.padding('6px', '10px'),
    fontWeight: tokens.fontWeightSemibold,
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke3),
  },
  chips: { display: 'flex', flexWrap: 'wrap', ...shorthands.gap('6px') },
  empty: {
    ...shorthands.padding('20px'),
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    backgroundColor: tokens.colorNeutralBackground3,
  },
  footer: {
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
    paddingTop: '10px',
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase300,
  },
});

function num(value: number): string {
  return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function money(value: number): string {
  return value.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  });
}

/** `15 of 412 rows`, or just `12 rows` when nothing was held back. */
function rowCountLabel(shown: number, total: number): string {
  if (total === 0) {
    return 'no rows';
  }
  const plural = total === 1 ? 'row' : 'rows';
  return shown < total ? `${String(shown)} of ${String(total)} ${plural}` : `${String(total)} ${plural}`;
}

export interface ReportPreviewDocumentProps {
  preview: ReportPreview;
  /**
   * How this report will be delivered, in the builder's words — "Every Wednesday at
   * 7:00am to abbye@…" or "Downloaded now". Rendered as the byline so the preview is
   * about a report that goes somewhere, not an anonymous grid.
   */
  deliveryLine: string;
  /**
   * Said under the title when the window shown is an EXAMPLE rather than the window
   * that will be used — a schedule derives its period from the day it sends, so the
   * preview can only show the current one.
   */
  periodNote?: string | undefined;
}

export function ReportPreviewDocument({
  preview,
  deliveryLine,
  periodNote,
}: ReportPreviewDocumentProps): JSX.Element {
  const styles = useStyles();
  const { summary } = preview;

  /*
   * The measures, in the order somebody reads them: how big is it, how much time is in
   * it, and what is still outstanding. Pay appears ONLY when the column is on the
   * report — a total shown beside a report that will not contain it is a figure
   * somebody will quote to a recipient who never sees it.
   */
  const measures: Array<[string, string]> = [
    ['Reporting window', preview.periodLabel],
    ['Employees covered', String(summary.employees)],
    ['Clock sessions', String(summary.entries)],
    ['Total hours', num(summary.totalHours)],
    ['Overtime hours', num(summary.overtimeHours)],
  ];
  if (summary.estimatedPay !== null) {
    measures.push(['Estimated pay (hours × rate)', money(summary.estimatedPay)]);
  }
  measures.push(
    ['Sessions awaiting approval', String(summary.unapproved)],
    ['Sessions not yet posted to NetSuite', String(summary.notPosted)],
    ['Sessions flagged for review', String(summary.flagged)],
    ['Worksheets in the spreadsheet', String(summary.tabCount)]
  );

  return (
    <div className={styles.page}>
      <div>
        <div className={styles.title}>{preview.name === '' ? 'Untitled report' : preview.name}</div>
        <Text className={styles.byline}>
          {preview.periodLabel} · {deliveryLine}
        </Text>
      </div>

      {periodNote === undefined ? null : <Text className={styles.lede}>{periodNote}</Text>}

      <div className={styles.section}>
        <div className={styles.heading}>Summary</div>
        <table className={styles.table}>
          <tbody>
            {measures.map(([label, value]) => (
              <tr key={label}>
                <td className={styles.measureKey}>{label}</td>
                <td className={styles.measureValue}>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={styles.section}>
        <div className={styles.heading}>Columns</div>
        <div className={styles.chips}>
          {preview.tables[0]?.columns.map((c) => (
            <Badge key={c.key} appearance="outline" color="informative">
              {c.header}
            </Badge>
          )) ?? <Text className={styles.muted}>No columns selected.</Text>}
        </div>
      </div>

      {preview.tables.length === 0 ? (
        /*
         * An empty report is INFORMATION, not a fault, and it is said in those words.
         * A blank panel here reads as "the preview is broken" and sends somebody
         * hunting through their configuration for a mistake that is not there.
         */
        <div className={styles.empty}>
          <Text>
            No time entries were clocked in this window for the employees this report
            covers. The report would still be produced, with a sheet saying so.
          </Text>
        </div>
      ) : (
        preview.tables.map((table) => (
          <div key={table.name} className={styles.section}>
            <div className={styles.subHeading}>
              <span className={styles.tabName}>{table.name}</span>
              <Text className={styles.muted}>{rowCountLabel(table.rows.length, table.totalRows)}</Text>
            </div>
            <div className={styles.scroller}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    {table.columns.map((c) => (
                      <th
                        key={c.key}
                        className={`${styles.th} ${c.numeric ? styles.numeric : ''}`}
                        scope="col"
                      >
                        {c.header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/*
                    Keyed by position: a preview row has no identity of its own, the
                    order is the server's, and the whole table is replaced on every
                    refresh — so there is nothing for React to reorder incorrectly.
                  */}
                  {table.rows.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {row.map((cell, cellIndex) => {
                        const column = table.columns[cellIndex];
                        return (
                          <td
                            key={column?.key ?? String(cellIndex)}
                            className={`${styles.td} ${column?.numeric === true ? styles.numeric : ''}`}
                          >
                            {typeof cell === 'number' ? num(cell) : cell}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}

      {preview.hiddenTables > 0 ? (
        <Text className={styles.muted}>
          + {preview.hiddenTables} more supervisor tab{preview.hiddenTables === 1 ? '' : 's'} not
          shown here. All of them are in the spreadsheet.
        </Text>
      ) : null}

      {preview.sections.map((section) => (
        <div key={section.reportId} className={styles.section}>
          <div className={styles.subHeading}>
            <span className={styles.tabName}>{section.label}</span>
            <Text className={styles.muted}>
              {rowCountLabel(section.rows.length, section.totalRows)}
            </Text>
          </div>
          {section.totalRows === 0 ? (
            <Text className={styles.muted}>
              Nothing entered in this window. The sheet is still included, so the report
              says so rather than leaving it out.
            </Text>
          ) : (
            <div className={styles.scroller}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    {section.headers.map((h) => (
                      <th key={h} className={styles.th} scope="col">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {section.rows.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {row.map((cell, cellIndex) => (
                        <td key={`${String(rowIndex)}-${String(cellIndex)}`} className={styles.td}>
                          {typeof cell === 'number' ? num(cell) : cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}

      {/* The workbook's own About sheet, so the preview ends where the file does. */}
      <div className={styles.footer}>
        Generated {preview.generatedOn} from WorkLogix. Hours are as clocked and approved;
        any estimated pay is hours × rate and does not model overtime multipliers or
        anything NetSuite applies at paycheck time.
      </div>
    </div>
  );
}
